import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/es/integration/react';
import { getPersistor } from '@rematch/persist';
import { Container } from 'react-bootstrap';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import * as Pages from './utils/route_pages';
import { getLocalStorage } from './utils/utilities';
import ApiUtils from './utils/api_utils';
/**
 * import Store
 */
import store from './store_models';
/**
 * Global CSS
 */
import './index.scss';
// import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/scss/bootstrap.scss';
import 'typeface-nunito';
/**
 * Import Component
 */
import App from './App';

import HomePage from './pages/HomePage';
import RewardPage from './pages/RewardPage';
import CreatePage from './pages/CreatePage';
import CreateReportPage from './pages/CreateReportPage';
import {
  SupportPage,
  SupportContactPage,
  SupportChatPage,
} from './pages/SupportPage';
import { AccountPage, MyQrCodePage, MyCardPage } from './pages/AccountPage';
import LoginPage from './pages/LoginPage';
import ForgotPage from './pages/ForgotPage';
import ChangePage from './pages/ChangePage';
import SettingPage from './pages/SettingPage';
import EditProfilPage from './pages/EditProfilPage';
import PointHistory from './pages/PointHistory';
import StarHistory from './pages/StarHistory';
import MyVoucher from './pages/MyVoucher';
import MyVoucherDetail from './pages/MyVoucher/Detail/voucher';
import VoucherPage from './pages/RewardPage/Reward/voucherPage';
import VoucherDetail from './pages/RewardPage/Reward/Detail/voucher';
import DiscountPage from './pages/RewardPage/Discount/discountPage';
import DiscountDetail from './pages/RewardPage/Discount/Detail/voucher';
import SummaryInfo from './pages/HomePage/summaryInfo';
import NearMePage from './pages/RewardPage/Discount/nearMePage';

import NotFoundPage from './pages/NotFoundPage';
import SplashScreen from './pages/SplashScreen';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermAndCondition from './pages/TermsAndCondition';
import UnderDevelopment from './pages/UnderDevelopment';
import AvatarEditor from './pages/EditProfilPage/avatar_editor';

import NewsPage from './pages/NewsPage';
import DetailNews from './pages/NewsPage/detail';
import HomePointSummary from './pages/HomePage/indexPointSummary';

import QuizPage from './pages/QuizPage';
import QuizDetailCategory from './pages/QuizPage/DetailCategory';
import QuizStart from './pages/QuizPage/StartQuestion';
import QuizResult from './pages/QuizPage/Result';

import SurveyPage from './pages/SurveyPage';
import SurveyStart from './pages/SurveyPage/SurveyStart';

import AnnouncementPage from './pages/AnnouncementPage';
import AnnouncementDetail from './pages/AnnouncementPage/AnnouncementDetail';
import AnnouncementListLikes from './pages/AnnouncementPage/AnnouncementListLikes';
import EventPage from './pages/EventPage';
import EventDetail from './pages/EventPage/detail';

import PollingPage from './pages/PollingPage';
import PollingFill from './pages/PollingPage/PollingFill';
import PollingResult from './pages/PollingPage/PollingResult';
import PollingComment from './pages/PollingPage/PollingComment';
import NoInternetConnection from './components/NoInternetConnection';

import LeaderboardPage from './pages/Leaderboard';
import BlockList from './pages/BlockListPage';

import Calendar from './pages/Calendar';
import AddReminder from './pages/Calendar/AddReminder';

import SnapReceiptPage from './pages/SnapReceiptPage';
import SnapReceiptPreviewPage from './pages/SnapReceiptPage/photoResult';
import SnapReceiptDetailtPage from './pages/SnapReceiptPage/detail';
import SnapReceiptSuccessSubmittedPage from './pages/SnapReceiptPage/successSubmitted';

// Register Sentry
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

if (process.env.REACT_APP_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
  });
}

import RewardCashTransfer from './pages/RewardPage/CashTransfer';
import ListBank from './pages/RewardPage/CashTransfer/listBank';
import Report from './pages/ReportPage/reportList';

function setDynamicTitleAndFavicon() {
  const retrievedObjectOrg = getLocalStorage('orgData', true);
  const faviconEl = document.getElementById('favicon');
  setStyle({});

  let data = {
    title: 'SKOR',
    favicon: `${process.env.PUBLIC_URL}/favicon.ico`,
  };

  if (retrievedObjectOrg) {
    const { favicon, name } = retrievedObjectOrg;

    setStyle(retrievedObjectOrg);

    data.title = name;
    data.favicon = favicon;
  }

  document.title = data.title;
  faviconEl.href = retrievedObjectOrg
    ? ApiUtils.validateImgUrl({
        url: data.favicon,
      })
    : data.favicon;
}

function setStyle(data) {
  const style = document.createElement('style');
  style.textContent = `
    :root {
      --main-color: ${
        Object.keys(data).length > 0 ? data.theme_color : '#FF5829'
      };
    }
  `;
  document.head.appendChild(style);
}

setDynamicTitleAndFavicon();

const root = ReactDOM.createRoot(document.getElementById('root'));
const persistor = getPersistor();
root.render(
  /**
   * to highlighting possible problems in a web application
   */
  <React.StrictMode>
    {/**
     * Put Store into Redux Provider
     */}
    <Provider store={store}>
      {/* load local storage store */}
      <PersistGate persistor={persistor} loading={<div>Loading...</div>}>
        {/**
         * Route Management
         */}
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<App />}>
              <Route index element={<SplashScreen />} />
              <Route path={Pages.reward} element={<RewardPage />} />
              <Route
                path={Pages.reward + '/:typeTab'}
                element={<RewardPage />}
              />
              <Route path={Pages.rewardPointSummary} element={<Container />} />
              <Route path={Pages.home} element={<HomePage />} />
              <Route path={Pages.create} element={<CreatePage />} />
              <Route
                path={Pages.createReport + '/:anonym'}
                element={<CreateReportPage />}
              />
              <Route path={Pages.support} element={<SupportPage />} />
              <Route
                path={Pages.supportContact}
                element={<SupportContactPage />}
              />
              <Route path={Pages.account} element={<AccountPage />} />
              <Route path={Pages.login} element={<LoginPage />} />
              <Route
                path={`${Pages.loginOrg}/:slug`}
                element={<SplashScreen />}
              />
              <Route
                path={`${Pages.loginOrgBySlug}/:slug`}
                element={<LoginPage />}
              />
              <Route path={Pages.forgotPassword} element={<ForgotPage />} />
              <Route path={Pages.changePassword} element={<ChangePage />} />
              <Route path={Pages.setting} element={<SettingPage />} />
              <Route path={Pages.editProfile} element={<EditProfilPage />} />
              <Route
                path={Pages.editAvatarProfile}
                element={<AvatarEditor />}
              />
              <Route path={Pages.showMyQrCode} element={<MyQrCodePage />} />
              <Route path={Pages.pointHistory} element={<PointHistory />} />
              <Route path={Pages.starHistory} element={<StarHistory />} />
              <Route
                path={Pages.pointHistory + '/:typeTab'}
                element={<PointHistory />}
              />
              <Route
                path={Pages.starHistory + '/:typeTab'}
                element={<StarHistory />}
              />
              <Route
                path={Pages.rewardVoucher + '/:url_category'}
                element={<VoucherPage />}
              />
              <Route
                path={Pages.rewardVoucherDetail}
                element={<VoucherDetail />}
              />
              <Route
                path={Pages.discountVoucher + '/:url_category'}
                element={<DiscountPage />}
              />
              <Route
                path={Pages.discountVoucherDetail}
                element={<DiscountDetail />}
              />
              <Route
                path={Pages.myVoucherDetail}
                element={<MyVoucherDetail />}
              />
              <Route path={Pages.supportChat} element={<SupportChatPage />} />
              <Route path={Pages.summaryInfo} element={<SummaryInfo />} />
              <Route path={Pages.news} element={<NewsPage />} />
              <Route
                path={`${Pages.detailNews}/:id`}
                element={<DetailNews />}
              />
              <Route
                path={Pages.homePointSummary}
                element={<HomePointSummary />}
              />
              <Route
                path={Pages.nearMe + '/:url_category'}
                element={<NearMePage />}
              />
              <Route path={Pages.quiz} element={<QuizPage />} />
              <Route
                path={Pages.quizCategory}
                element={<QuizDetailCategory />}
              />
              <Route path={Pages.quizQuestion} element={<QuizStart />} />
              <Route path={Pages.quizResult} element={<QuizResult />} />
              <Route path={Pages.survey} element={<SurveyPage />} />
              <Route path={Pages.surveyQuestion} element={<SurveyStart />} />
              <Route path="*" element={<NotFoundPage />} />
              <Route path={Pages.showMyCard} element={<MyCardPage />} />
              <Route path={Pages.myVoucher} element={<MyVoucher />} />
              <Route path={Pages.privacy_policy} element={<PrivacyPolicy />} />
              <Route
                path={Pages.terms_and_condition}
                element={<TermAndCondition />}
              />
              <Route
                path={Pages.underDevelopment}
                element={<UnderDevelopment />}
              />
              <Route path={Pages.quiz} element={<QuizPage />} />
              <Route
                path={Pages.quizCategory}
                element={<QuizDetailCategory />}
              />
              <Route path={Pages.quizQuestion} element={<QuizStart />} />
              <Route path={Pages.quizResult} element={<QuizResult />} />

              <Route path={Pages.announcement} element={<AnnouncementPage />} />
              <Route
                path={Pages.announcementDetail}
                element={<AnnouncementDetail />}
              />
              <Route
                path={Pages.announcementListLikes}
                element={<AnnouncementListLikes />}
              />
              <Route path={Pages.polling} element={<PollingPage />} />
              <Route path={Pages.pollingFill} element={<PollingFill />} />
              <Route path={Pages.pollingResult} element={<PollingResult />} />
              <Route path={Pages.pollingComment} element={<PollingComment />} />
              <Route path={Pages.event} element={<EventPage />} />
              <Route
                path={`${Pages.eventDetail}/:id`}
                element={<EventDetail />}
              />
              <Route path={Pages.leaderboard} element={<LeaderboardPage />} />
              <Route path={Pages.blocklist} element={<BlockList />} />

              <Route
                path={Pages.noInternetConnection}
                element={<NoInternetConnection />}
              />
              <Route
                path={Pages.cashTransfer}
                element={<RewardCashTransfer />}
              />
              <Route path={Pages.listBank} element={<ListBank />} />
              <Route path={Pages.report} element={<Report />} />
              <Route path={Pages.calendar} element={<Calendar />} />
              <Route
                path={Pages.calendarAddReminder}
                element={<AddReminder />}
              />
              <Route path={Pages.snapReceipt} element={<SnapReceiptPage />} />
              <Route
                path={`${Pages.snapReceipt}/preview`}
                element={<SnapReceiptPreviewPage />}
              />
              <Route
                path={`${Pages.snapReceipt}/detail`}
                element={<SnapReceiptDetailtPage />}
              />
              <Route
                path={`${Pages.snapReceipt}/detail/:id`}
                element={<SnapReceiptDetailtPage />}
              />
              <Route
                path={`${Pages.snapReceipt}/success-submitted`}
                element={<SnapReceiptSuccessSubmittedPage />}
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
